import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Clock, Timer, AlertCircle, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PackingTimeEstimatorProps {
  tripLength: number;
  destination: string;
  tripType: string;
  packingItems: any[];
  startDate: Date;
  endDate: Date;
  packingStyle?: string;
  travelingWith?: string;
}

interface TimeBreakdown {
  category: string;
  minutes: number;
  description: string;
}

export function PackingTimeEstimator({
  tripLength,
  destination,
  tripType,
  packingItems,
  startDate,
  endDate,
  packingStyle = 'balanced',
  travelingWith = 'solo'
}: PackingTimeEstimatorProps) {
  const [estimatedTime, setEstimatedTime] = useState(0);
  const [timeBreakdown, setTimeBreakdown] = useState<TimeBreakdown[]>([]);
  const [complexity, setComplexity] = useState<'simple' | 'moderate' | 'complex'>('moderate');
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    calculatePackingTime();
  }, [tripLength, destination, tripType, packingItems, startDate, endDate, packingStyle, travelingWith]);

  const calculatePackingTime = () => {
    let totalMinutes = 0;
    const breakdown: TimeBreakdown[] = [];

    // Define packing categories with realistic time estimates based on actual packing behavior
    const packingCategories = [
      { 
        name: 'Clothes', 
        timePerItem: 0.75, // ~45 seconds per item (folding/rolling)
        estimatedItems: Math.max(8, Math.min(25, tripLength * 2.5)), // Realistic clothing count per day
        icon: '👕'
      },
      { 
        name: 'Shoes', 
        timePerItem: 2, // 2 min per pair (checking, cleaning, packing)
        estimatedItems: Math.min(4, Math.max(2, Math.ceil(tripLength / 4) + 1)), // 2-4 pairs
        icon: '👟'
      },
      { 
        name: 'Toiletries', 
        timePerItem: 0.4, // ~25 seconds per item (quick grab and pack)
        estimatedItems: Math.max(8, Math.min(15, 8 + Math.ceil(tripLength / 3))), // 8-15 items
        icon: '🧴'
      },
      { 
        name: 'Electronics', 
        timePerItem: 2, // 2 min (finding cables, checking, packing safely)
        estimatedItems: Math.max(4, Math.min(7, 4 + Math.ceil(tripLength / 7))), // 4-7 items
        icon: '📱'
      },
      { 
        name: 'Documents', 
        timePerItem: 0.5, // 30 seconds (checking and organizing)
        estimatedItems: Math.max(4, Math.min(8, 4 + (destination.toLowerCase().includes('usa') ? 0 : 2))), // More for international
        icon: '📄'
      },
      { 
        name: 'Accessories', 
        timePerItem: 0.6, // ~35 seconds per accessory
        estimatedItems: Math.max(3, Math.min(10, 3 + Math.ceil(tripLength * 0.5))), // 3-10 items
        icon: '👜'
      }
    ];

    // Add special categories based on trip type
    if (tripType.toLowerCase() === 'business') {
      packingCategories.push({
        name: 'Business Items',
        timePerItem: 1.5, // Suits/blazers need careful folding
        estimatedItems: Math.max(4, Math.min(8, 4 + Math.ceil(tripLength * 0.5))),
        icon: '💼'
      });
    } else if (tripType.toLowerCase() === 'adventure') {
      packingCategories.push({
        name: 'Outdoor Gear',
        timePerItem: 2.5, // Specialized gear takes time
        estimatedItems: Math.max(5, Math.min(12, 5 + Math.ceil(tripLength * 0.6))),
        icon: '🎒'
      });
    } else if (tripType.toLowerCase() === 'beach') {
      packingCategories.push({
        name: 'Beach Items',
        timePerItem: 1, // Swimwear, towels, sunscreen
        estimatedItems: Math.max(4, Math.min(8, 4 + Math.ceil(tripLength * 0.4))),
        icon: '🏖️'
      });
    }

    // Add medicine category for longer trips or international travel
    const isInternational = !destination.toLowerCase().includes('usa') && 
                           !destination.toLowerCase().includes('united states') &&
                           !destination.toLowerCase().includes('us');
                           
    if (tripLength > 5 || isInternational) {
      packingCategories.push({
        name: 'Medicines',
        timePerItem: 0.8, // Quick to check and pack pills/bottles
        estimatedItems: Math.max(3, Math.min(8, 3 + Math.ceil(tripLength / 4))),
        icon: '💊'
      });
    }

    // Calculate time for each category
    packingCategories.forEach(category => {
      const categoryTime = Math.round(category.estimatedItems * category.timePerItem);
      totalMinutes += categoryTime;
      
      breakdown.push({
        category: `${category.icon} ${category.name}`,
        minutes: categoryTime,
        description: `${Math.round(category.estimatedItems)} items × ${category.timePerItem} min`
      });
    });

    // Add base organization time (laying out items, organizing, final check)
    const organizationTime = Math.round(10 + (tripLength * 1.5)); // 10-25 min base organization
    totalMinutes += organizationTime;
    breakdown.push({
      category: '📋 Planning & Organization',
      minutes: organizationTime,
      description: 'Layout, organization, final check'
    });

    // Add packing style adjustment
    let styleAdjustment = 0;
    switch (packingStyle) {
      case 'minimalist':
        styleAdjustment = -Math.round(totalMinutes * 0.2); // 20% faster
        breakdown.push({
          category: '⚡ Quick Style',
          minutes: styleAdjustment,
          description: 'Efficient minimalist approach'
        });
        break;
      case 'thorough':
        styleAdjustment = Math.round(totalMinutes * 0.3); // 30% more time
        breakdown.push({
          category: '🎯 Thorough Style',
          minutes: styleAdjustment,
          description: 'Careful organization & checking'
        });
        break;
      default: // balanced
        styleAdjustment = Math.round(totalMinutes * 0.1); // 10% buffer
        breakdown.push({
          category: '⚖️ Balanced Approach',
          minutes: styleAdjustment,
          description: 'Standard packing method'
        });
    }
    totalMinutes += styleAdjustment;

    // Add family/partner adjustment
    if (travelingWith === 'family') {
      const familyTime = Math.round(totalMinutes * 0.35); // 35% increase for family coordination
      totalMinutes += familyTime;
      breakdown.push({
        category: '👨‍👩‍👧‍👦 Family Items',
        minutes: familyTime,
        description: 'Additional family coordination'
      });
    } else if (travelingWith === 'partner') {
      const partnerTime = Math.round(totalMinutes * 0.15); // 15% increase for coordination
      totalMinutes += partnerTime;
      breakdown.push({
        category: '👫 Shared Items',
        minutes: partnerTime,
        description: 'Coordinating shared items'
      });
    }

    // Set complexity based on total time (adjusted thresholds)
    if (totalMinutes > 75) {
      setComplexity('complex');
    } else if (totalMinutes > 40) {
      setComplexity('moderate');
    } else {
      setComplexity('simple');
    }

    // Ensure minimum time
    totalMinutes = Math.max(totalMinutes, 25);
    
    setEstimatedTime(Math.round(totalMinutes));
    setTimeBreakdown(breakdown.filter(item => item.minutes !== 0));
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };

  const getComplexityIcon = () => {
    switch (complexity) {
      case 'simple':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'moderate':
        return <Timer className="h-4 w-4 text-yellow-500" />;
      case 'complex':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
    }
  };

  const getComplexityColor = () => {
    switch (complexity) {
      case 'simple': return 'text-green-600';
      case 'moderate': return 'text-yellow-600';
      case 'complex': return 'text-orange-600';
    }
  };

  const getComplexityLabel = () => {
    switch (complexity) {
      case 'simple': return 'Quick Pack';
      case 'moderate': return 'Standard';
      case 'complex': return 'Detailed';
    }
  };

  return (
    <Card className="border-cyan-500/20 shadow-md shadow-cyan-500/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer transition-colors bg-gradient-to-br from-cyan-50/60 to-transparent dark:from-cyan-950/15 dark:to-transparent hover:from-cyan-50 dark:hover:from-cyan-950/25">
            <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-sm shadow-cyan-500/20">
                <Clock className="h-4 w-4" />
              </span>
              Packing Time Estimate
              {isOpen ? (
                <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm pl-11">
              Estimated time needed to pack for your trip
            </CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Main Time Display */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                {getComplexityIcon()}
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {formatTime(estimatedTime)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Total estimated time
                  </div>
                </div>
              </div>
              <Badge variant="outline" className={cn("text-xs", getComplexityColor())}>
                {getComplexityLabel()}
              </Badge>
            </div>

            {/* Time Breakdown */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Time Breakdown</h4>
              <div className="space-y-2">
                {timeBreakdown.map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ 
                          backgroundColor: item.minutes > 0 
                            ? `hsl(${index * 45}, 65%, 50%)` 
                            : 'hsl(120, 65%, 50%)'
                        }}
                      />
                      <span>{item.category}</span>
                    </div>
                    <div className="flex items-center gap-2 text-right">
                      <span className={cn(
                        "font-medium",
                        item.minutes > 0 ? "text-foreground" : "text-green-600"
                      )}>
                        {item.minutes > 0 ? '+' : ''}{formatTime(Math.abs(item.minutes))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-xs text-muted-foreground">
                💡 Start packing 1-2 days before departure for stress-free travel. 
                {complexity === 'complex' && ' Consider making a detailed checklist for complex trips.'}
                {complexity === 'simple' && ' Quick and easy - you can pack the night before!'}
              </p>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}